import React, { useEffect, useState } from 'react';
import { MotionAnchor, MotionCard, FadeInSection } from '../ui/MotionPrimitives';

export type ListingType = 'all' | 'sale' | 'rent';
export type PropertyType = 'all' | 'apartments' | 'villas' | 'plots' | 'hotels' | 'projects_offplan';

interface Property {
  id: number;
  status: ListingType; // sale or rent (all = no filter)
  type: string;
  propertyType: PropertyType;
  price: string;
  priceLabel: string;
  title: string;
  location: string;
  beds: string;
  baths: string;
  area: string;
  description?: string;
  yearBuilt?: number;
  parking?: string;
  amenities?: string[];
  image: string;
  imageLocation?: string;
  imageBedrooms?: string;
  imageMore?: string;
}

interface PropertiesResultsProps {
  listingType: ListingType;
  propertyType: PropertyType;
  highlightId?: number;
  locationQuery?: string;
  minPrice?: number;
  maxPrice?: number;
}

// Helper component for images with fallback URLs
const ImageWithFallback: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackUrls?: string[];
}> = ({ src, alt, className, fallbackUrls = [] }) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [errorCount, setErrorCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [allUrlsTried, setAllUrlsTried] = useState<string[]>([]);

  // Simple file ID extractor for error message
  const extractFileId = (url: string): string | null => {
    const match = url.match(/[?&]id=([a-zA-Z0-9_-]{25,})|\/file\/d\/([a-zA-Z0-9_-]{25,})|\/d\/([a-zA-Z0-9_-]{25,})/);
    return match ? (match[1] || match[2] || match[3] || null) : null;
  };

  const handleError = () => {
    const nextIndex = errorCount;
    if (nextIndex < fallbackUrls.length) {
      const nextUrl = fallbackUrls[nextIndex];
      setAllUrlsTried(prev => [...prev, currentSrc]);
      setCurrentSrc(nextUrl);
      setErrorCount(nextIndex + 1);
      console.log(`Trying fallback URL ${nextIndex + 1}/${fallbackUrls.length}:`, nextUrl);
    } else {
      // All fallbacks exhausted
      setHasError(true);
      setAllUrlsTried(prev => [...prev, currentSrc]);
      const fileId = extractFileId(src);
      const allTried = [...allUrlsTried, currentSrc];
      
      console.error(`❌ ALL IMAGE URLs FAILED: ${alt}`, {
        fileId: fileId || 'NOT EXTRACTED',
        originalUrl: src.substring(0, 100) + (src.length > 100 ? '...' : ''),
        totalAttempts: allTried.length,
        triedUrls: allTried,
        possibleIssues: [
          fileId ? 'File may not be shared with "Anyone with the link"' : 'File ID could not be extracted',
          'File might be in a different Google account',
          'File might be deleted or moved',
          'File type might not be supported for direct viewing'
        ],
        testUrl: fileId ? `https://drive.google.com/file/d/${fileId}/view` : null
      });
    }
  };

  // Reset when src changes
  useEffect(() => {
    setCurrentSrc(src);
    setErrorCount(0);
    setHasError(false);
    setAllUrlsTried([]);
  }, [src]);

  if (hasError) {
    // Extract file ID for testing
    const fileId = extractFileId(src);
    const testUrl = fileId 
      ? `https://drive.google.com/file/d/${fileId}/view`
      : null;
    
    // Show placeholder when all URLs fail
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center`}>
        <div className="text-center p-4">
          <div className="text-gray-400 text-xs mb-1">Image unavailable</div>
          <div className="text-gray-300 text-[10px] mb-2">File not shared publicly</div>
          {testUrl && (
            <a 
              href={testUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D88546] text-[10px] underline hover:text-[#e59252]"
              onClick={(e) => e.stopPropagation()}
            >
              Test file access
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={(e) => {
        const img = e.currentTarget;
        console.warn(`Image failed to load (attempt ${errorCount + 1}):`, {
          url: currentSrc,
          alt,
          errorCount,
          remainingFallbacks: fallbackUrls.length - errorCount,
          allFallbacks: fallbackUrls,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          complete: img.complete
        });
        handleError();
      }}
      onLoad={(e) => {
        const img = e.currentTarget;
        if (errorCount > 0) {
          console.log(`✅ Image loaded successfully with fallback ${errorCount}:`, {
            url: currentSrc,
            alt,
            fileId: extractFileId(currentSrc),
            dimensions: `${img.naturalWidth}x${img.naturalHeight}`
          });
        } else {
          console.log(`✅ Image loaded successfully (primary URL):`, {
            url: currentSrc,
            alt,
            fileId: extractFileId(currentSrc),
            dimensions: `${img.naturalWidth}x${img.naturalHeight}`
          });
        }
      }}
      loading="lazy"
    />
  );
};

const PropertiesResults: React.FC<PropertiesResultsProps> = ({
  listingType,
  propertyType,
  highlightId,
  locationQuery,
  minPrice,
  maxPrice,
}) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  // Auto-open modal when highlightId is provided (e.g. from Home featured listings)
  useEffect(() => {
    if (!highlightId || !properties.length) return;
    const match = properties.find((p: Property) => p.id === highlightId);
    if (match) {
      setSelectedProperty(match);
    }
  }, [highlightId, properties]);

  const getCellValue = (row: any, target: string) => {
    if (!row || typeof row !== 'object') return undefined;
    const key = Object.keys(row).find(
      (k) => k.trim().toLowerCase() === target,
    );
    return key ? row[key] : undefined;
  };

  // Extract file ID from Google Drive URL
  const extractDriveFileId = (url: string): string | null => {
    if (!url || typeof url !== 'string') return null;
    
    try {
      // Clean URL first - remove any whitespace, newlines, and extract URL from iframe if present
      let cleanUrl = url.trim().replace(/\s+/g, '');
      
      // Handle iframe embed codes: extract the src URL
      // Example: <iframe src="https://drive.google.com/file/d/FILE_ID/preview" ...></iframe>
      const iframeMatch = cleanUrl.match(/src=["']([^"']+)["']/);
      if (iframeMatch && iframeMatch[1]) {
        cleanUrl = iframeMatch[1];
        console.log('📦 Extracted URL from iframe code');
      }
      
      // Pattern 1: /file/d/FILE_ID/view, /file/d/FILE_ID/preview, or /file/d/FILE_ID/edit
      // Handles:
      // - Share links: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
      // - Preview links: https://drive.google.com/file/d/FILE_ID/preview
      // - Iframe embeds: <iframe src="https://drive.google.com/file/d/FILE_ID/preview" ...>
      // Regex: matches /file/d/ followed by file ID (25+ chars)
      let match = cleanUrl.match(/\/file\/d\/([a-zA-Z0-9_-]{25,})/);
      if (match && match[1]) {
        const fileId = match[1];
        const urlType = cleanUrl.includes('/preview') ? 'preview' : 
                       cleanUrl.includes('/view') ? 'share link' : 
                       cleanUrl.includes('/edit') ? 'edit link' : 'file link';
        console.log(`✅ Extracted file ID from ${urlType} (pattern 1):`, { 
          url: cleanUrl.substring(0, 80), 
          fileId,
          fileIdLength: fileId.length
        });
        return fileId;
      }
      
      // Pattern 2: ?id=FILE_ID
      match = url.match(/[?&]id=([a-zA-Z0-9_-]{25,})/);
      if (match && match[1]) {
        const fileId = match[1];
        console.log('✅ Extracted file ID (pattern 2):', { url: url.substring(0, 80), fileId });
        return fileId;
      }
      
      // Pattern 3: /d/FILE_ID
      match = url.match(/\/d\/([a-zA-Z0-9_-]{25,})/);
      if (match && match[1]) {
        const fileId = match[1];
        console.log('✅ Extracted file ID (pattern 3):', { url: url.substring(0, 80), fileId });
        return fileId;
      }
      
      // Fallback: Parse URL manually
      const parsed = new URL(url);
      if (parsed.pathname.includes('/file/d/')) {
        const parts = parsed.pathname.split('/');
        const dIndex = parts.indexOf('d');
        if (dIndex !== -1 && parts[dIndex + 1]) {
          const fileId = parts[dIndex + 1];
          console.log('✅ Extracted file ID (fallback):', { url: url.substring(0, 80), fileId });
          return fileId;
        }
      }
      
      const idFromParams = parsed.searchParams.get('id');
      if (idFromParams) {
        console.log('✅ Extracted file ID (query param):', { url: url.substring(0, 80), fileId: idFromParams });
        return idFromParams;
      }
      
      console.warn('❌ Could not extract file ID from URL:', url.substring(0, 100));
      return null;
    } catch (error) {
      console.warn('Error extracting Drive file ID from URL:', url, error);
      return null;
    }
  };

  const formatImageUrl = (rawUrl?: string) => {
    if (!rawUrl) {
      console.warn('formatImageUrl: No URL provided');
      return '';
    }
    
    // Clean the URL - remove whitespace, newlines, and any extra characters
    let url = String(rawUrl).trim();
    
    // Check if it's an iframe embed code and extract the src URL
    if (url.includes('<iframe') && url.includes('src=')) {
      const iframeMatch = url.match(/src=["']([^"']*drive\.google\.com[^"']*)["']/i);
      if (iframeMatch && iframeMatch[1]) {
        url = iframeMatch[1];
        console.log('📦 Extracted URL from iframe embed code');
      }
    }
    
    // Clean whitespace and newlines
    url = url.replace(/\s+/g, '').replace(/\n/g, '');
    
    if (!url) {
      console.warn('formatImageUrl: Empty URL after cleaning');
      return '';
    }

    if (url.includes('drive.google.com')) {
      // Check if URL is already a thumbnail or uc?export URL - use it directly
      if (url.includes('/thumbnail?id=') || url.includes('/uc?export=')) {
        console.log('✅ Google Drive URL already in correct format, using directly');
        return url;
      }
      
      // Handle all Google Drive link formats:
      // - Share links: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
      // - Preview links: https://drive.google.com/file/d/FILE_ID/preview
      // - Iframe embeds: <iframe src="https://drive.google.com/file/d/FILE_ID/preview" ...>
      const fileId = extractDriveFileId(url);
      if (fileId) {
        // Convert any Google Drive link to thumbnail URL - this works for images
        const imageUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
        console.log('✅ Converted Google Drive link to thumbnail URL:', { 
          original: url.substring(0, 100), 
          fileId, 
          imageUrl
        });
        return imageUrl;
      } else {
        console.error('❌ formatImageUrl: Could not extract file ID from Google Drive link:', url.substring(0, 100));
        // Return original URL as fallback
        return url;
      }
    }

    // Return original URL if not a Google Drive URL
    return url.startsWith('http') ? url : `${window.location.origin}${url}`;
  };

  // Get alternative URLs for error fallback
  const getAlternativeImageUrl = (rawUrl?: string): string[] => {
    if (!rawUrl) return [];
    const url = rawUrl.trim();
    if (!url.includes('drive.google.com')) return [];
    
    const fileId = extractDriveFileId(url);
    if (!fileId) {
      console.warn('getAlternativeImageUrl: Could not extract file ID');
      return [];
    }
    
    // Check if primary URL is already thumbnail - if so, skip it in fallbacks
    const isAlreadyThumbnail = url.includes('/thumbnail?id=');
    
    // Try different URL formats that work for shared Google Drive files
    const fallbacks: string[] = [];
    
    // uc?export=view - try this first as fallback (sometimes works when thumbnail doesn't)
    fallbacks.push(`https://drive.google.com/uc?export=view&id=${fileId}`);
    
    // Only add thumbnail fallbacks if primary wasn't already a thumbnail
    if (!isAlreadyThumbnail) {
      fallbacks.push(`https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`);
    }
    
    // Larger thumbnail sizes
    fallbacks.push(`https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`);
    fallbacks.push(`https://drive.google.com/thumbnail?id=${fileId}&sz=w4000`);
    
    // Download URL (last resort - may trigger download)
    fallbacks.push(`https://drive.google.com/uc?export=download&id=${fileId}`);
    
    return fallbacks;
  };

  // Fetch live properties from Google Sheets via Apps Script web app
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        // Choose the sheet tab based on listing type
        const sheetName =
          listingType === 'sale' ? 'Sale' : listingType === 'rent' ? 'Rent' : 'All';

        const res = await fetch(
          `https://script.google.com/macros/s/AKfycbyvxxFdNiHzmY1hV-ViBjkDAZ8KKE_M8pnKssxQVfY2FJYBZH1wLSW0rHvsMw0dv0Dn5A/exec?sheet=${encodeURIComponent(
            sheetName,
          )}`,
        );
        if (!res.ok) return;

        const rows = await res.json();
        if (!Array.isArray(rows)) return;

        const mapped: Property[] = rows
          .map((row: any): Property | null => {
            if (!row) return null;

            const imageValue = getCellValue(row, 'image');
            const imageLocationValue = getCellValue(row, 'imagelocation');
            const imageBedroomsValue = getCellValue(row, 'imagebedrooms');
            const imageMoreValue = getCellValue(row, 'imagemore');

            // Log image URLs for debugging
            if (imageValue) {
              const imageStr = String(imageValue);
              const isDriveUrl = imageStr.includes('drive.google.com');
              const fileId = isDriveUrl ? extractDriveFileId(imageStr) : null;
              console.log('Property image URL:', {
                propertyId: row.id,
                title: row.title,
                hasImage: !!imageValue,
                isDriveUrl,
                fileId: fileId || 'NOT EXTRACTED',
                originalUrl: imageStr.substring(0, 100) + (imageStr.length > 100 ? '...' : '')
              });
            }

            // Status comes from the sheet; default to 'sale' if missing
            const statusRaw = String(row.status || '').toLowerCase();
            const status: ListingType =
              statusRaw === 'sale' || statusRaw === 'rent'
                ? (statusRaw as ListingType)
                : 'sale';

            // Derive propertyType from the "type" field
            const typeStr = String(row.type || '').toLowerCase();
            let propertyType: PropertyType = 'apartments';
            if (typeStr.includes('villa')) propertyType = 'villas';
            else if (typeStr.includes('plot')) propertyType = 'plots';
            else if (typeStr.includes('hotel')) propertyType = 'hotels';
            else if (typeStr.includes('project') || typeStr.includes('offplan'))
              propertyType = 'projects_offplan';
            else if (typeStr.includes('apartment')) propertyType = 'apartments';

            // amenities can be array (from script) or comma-separated string
            const amenitiesFromSheet: string[] | undefined = Array.isArray(row.amenities)
              ? (row.amenities as string[])
              : typeof row.amenities === 'string' && row.amenities
              ? String(row.amenities)
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : undefined;

            return {
              id: Number(row.id || 0),
              status,
              type: String(row.type || ''),
              propertyType,
              price: String(row.price || ''),
              priceLabel: String(row.priceLabel || ''),
              title: String(row.title || ''),
              location: String(row.location || ''),
              beds: String(row.beds || ''),
              baths: String(row.baths || ''),
              area: String(row.area || ''),
              description: row.description ? String(row.description) : undefined,
              yearBuilt: row.yearBuilt ? Number(row.yearBuilt) : undefined,
              parking: row.parking ? String(row.parking) : undefined,
              amenities: amenitiesFromSheet,
              image: imageValue ? String(imageValue) : '',
              imageLocation: imageLocationValue ? String(imageLocationValue) : undefined,
              imageBedrooms: imageBedroomsValue ? String(imageBedroomsValue) : undefined,
              imageMore: imageMoreValue ? String(imageMoreValue) : undefined,
            };
          })
          .filter((p): p is Property => p !== null);

        setProperties(mapped);
        
        // Log summary of all images for debugging
        console.group('📊 Image URL Analysis Summary');
        const imageAnalysis = mapped.map(prop => {
          const mainImageId = prop.image ? extractDriveFileId(prop.image) : null;
          const locationId = prop.imageLocation ? extractDriveFileId(prop.imageLocation) : null;
          const bedroomsId = prop.imageBedrooms ? extractDriveFileId(prop.imageBedrooms) : null;
          const moreId = prop.imageMore ? extractDriveFileId(prop.imageMore) : null;
          
          return {
            propertyId: prop.id,
            title: prop.title,
            mainImage: { hasUrl: !!prop.image, fileId: mainImageId, url: prop.image?.substring(0, 80) + '...' },
            locationImage: { hasUrl: !!prop.imageLocation, fileId: locationId },
            bedroomsImage: { hasUrl: !!prop.imageBedrooms, fileId: bedroomsId },
            moreImage: { hasUrl: !!prop.imageMore, fileId: moreId },
            allFileIdsExtracted: !!(mainImageId && locationId && bedroomsId && moreId)
          };
        });
        
        const withFileIds = imageAnalysis.filter(p => p.mainImage.fileId);
        const withoutFileIds = imageAnalysis.filter(p => !p.mainImage.fileId);
        
        console.log(`Total properties: ${mapped.length}`);
        console.log(`Properties with extractable file IDs: ${withFileIds.length}`);
        console.log(`Properties without extractable file IDs: ${withoutFileIds.length}`);
        
        if (withoutFileIds.length > 0) {
          console.warn('⚠️ Properties with unextractable file IDs:', withoutFileIds);
        }
        
        console.log('All image details:', imageAnalysis);
        console.groupEnd();
      } catch {
        // On error, show nothing rather than crashing
        setProperties([]);
      }
    };

    fetchProperties();
  }, [listingType]);

  const openModal = (property: Property) => setSelectedProperty(property);
  const closeModal = () => setSelectedProperty(null);

  const filtered = properties.filter((property) => {
    const matchesListing =
      listingType === 'all' ? true : property.status === listingType;

    const matchesPropertyType =
      propertyType === 'all' ? true : property.propertyType === propertyType;

    const matchesLocation = locationQuery
      ? property.location.toLowerCase().includes(locationQuery.toLowerCase())
      : true;

    let matchesPrice = true;
    if (minPrice !== undefined || maxPrice !== undefined) {
      const numeric = Number(
        String(property.price || '')
          .replace(/[^0-9.]/g, '')
          .trim(),
      );

      if (!Number.isNaN(numeric)) {
        if (minPrice !== undefined && numeric < minPrice) {
          matchesPrice = false;
        }
        if (maxPrice !== undefined && numeric > maxPrice) {
          matchesPrice = false;
        }
      }
    }

    return matchesListing && matchesPropertyType && matchesLocation && matchesPrice;
  });

  return (
    <FadeInSection className="w-full bg-white py-14">
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black">
              Properties
            </p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-black md:text-3xl">
              Available Listings
            </h2>
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
            Showing: {listingType === 'all' ? 'All' : listingType === 'sale' ? 'For Sale' : 'For Rent'}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((property) => (
            <MotionCard
              key={property.id}
              onClick={() => openModal(property)}
              className="overflow-hidden rounded-2xl bg-white shadow-md cursor-pointer"
            >
              <div className="relative h-52 w-full">
                <ImageWithFallback
                  src={formatImageUrl(property.image)}
                  alt={property.title}
                  className="h-full w-full object-cover"
                  fallbackUrls={getAlternativeImageUrl(property.image)}
                />
                <div className="absolute left-3 top-3 rounded-full bg-black/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
                  {property.status === 'sale' ? 'For Sale' : 'For Rent'}
                </div>
              </div>

              <div className="space-y-1 px-4 py-4">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#D88546]">
                  {property.type}
                </p>
                <h3 className="text-base font-semibold text-black">{property.title}</h3>
                <p className="text-xs text-black/70">{property.location}</p>
                <p className="mt-2 text-lg font-bold text-black">{property.price || property.priceLabel}</p>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-black/70">
                  <span>{property.beds}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>{property.baths}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>{property.area}</span>
                </div>
              </div>
            </MotionCard>
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedProperty && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={closeModal}
        >
          <div
            className="flex w-full max-w-4xl max-h-[90vh] flex-col rounded-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex flex-shrink-0 items-start justify-between p-6 md:p-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#D88546]">
                  {selectedProperty.type}
                </p>
                <h2 className="mt-1 text-xl font-semibold text-black md:text-2xl">
                  {selectedProperty.title}
                </h2>
                <p className="mt-1 text-sm text-black/70">{selectedProperty.location}</p>
              </div>
              <button
                onClick={closeModal}
                className="rounded-full bg-gray-100 p-2 text-black transition hover:bg-gray-200"
              >
                <span className="block h-5 w-5 text-2xl leading-none">×</span>
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 md:px-8">
              {/* Image carousel */}
              <div className="flex flex-col gap-4 pb-6 md:flex-row">
                <div className="flex-1">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                    Location View
                  </h3>
                  <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                    <ImageWithFallback
                      src={formatImageUrl(selectedProperty.imageLocation || selectedProperty.image)}
                      alt="Location view"
                      className="h-full w-full object-cover"
                      fallbackUrls={getAlternativeImageUrl(selectedProperty.imageLocation || selectedProperty.image)}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                    Bedrooms
                  </h3>
                  <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                    <ImageWithFallback
                      src={formatImageUrl(selectedProperty.imageBedrooms || selectedProperty.image)}
                      alt="Bedrooms"
                      className="h-full w-full object-cover"
                      fallbackUrls={getAlternativeImageUrl(selectedProperty.imageBedrooms || selectedProperty.image)}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                    More
                  </h3>
                  <div className="relative h-48 w-full overflow-hidden rounded-xl bg-gray-100">
                    <ImageWithFallback
                      src={formatImageUrl(selectedProperty.imageMore || selectedProperty.image)}
                      alt="More details"
                      className="h-full w-full object-cover"
                      fallbackUrls={getAlternativeImageUrl(selectedProperty.imageMore || selectedProperty.image)}
                    />
                  </div>
                </div>
              </div>

              {/* Details and price */}
              <div className="flex flex-col gap-4 pb-6 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4 text-sm text-black/70">
                  <span>{selectedProperty.beds}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>{selectedProperty.baths}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>{selectedProperty.area}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>Built: {selectedProperty.yearBuilt}</span>
                  <span className="h-0.5 w-0.5 rounded-full bg-black/30" />
                  <span>{selectedProperty.parking}</span>
                </div>
                <div className="text-lg font-bold text-black md:text-xl">
                  {selectedProperty.price || selectedProperty.priceLabel}
                </div>
              </div>

              {/* Description */}
              {selectedProperty.description && (
                <div className="border-t pb-6 pt-4">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                    Description
                  </h3>
                  <p className="text-sm leading-relaxed text-black/80">
                    {selectedProperty.description}
                  </p>
                </div>
              )}

              {/* Amenities */}
              {selectedProperty.amenities && selectedProperty.amenities.length > 0 && (
                <div className="border-t pb-6 pt-4">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-black">
                    Amenities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProperty.amenities.map((amenity, idx) => (
                      <span
                        key={idx}
                        className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-black"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Fixed footer CTA */}
            <div className="flex-shrink-0 border-t p-6 md:px-8">
              <MotionAnchor
                href={`https://wa.me/971508401805?text=${encodeURIComponent(
                  `Hi, I'm interested in this property:\n\n` +
                  `Title: ${selectedProperty.title}\n` +
                  `Type: ${selectedProperty.type}\n` +
                  `Location: ${selectedProperty.location}\n` +
                  `Price: ${selectedProperty.price || selectedProperty.priceLabel}\n` +
                  `Details: ${selectedProperty.beds} · ${selectedProperty.baths} · ${selectedProperty.area}\n` +
                  `Year Built: ${selectedProperty.yearBuilt}\n` +
                  `Parking: ${selectedProperty.parking}\n` +
                  `Description: ${selectedProperty.description}\n` +
                  `Amenities: ${selectedProperty.amenities?.join(', ') || 'N/A'}\n\n` +
                  `Images:\n` +
                  `• Main: ${formatImageUrl(selectedProperty.image)}\n` +
                  `• Location: ${formatImageUrl(selectedProperty.imageLocation || selectedProperty.image)}\n` +
                  `• Bedrooms: ${formatImageUrl(selectedProperty.imageBedrooms || selectedProperty.image)}\n` +
                  `• More: ${formatImageUrl(selectedProperty.imageMore || selectedProperty.image)}\n\n` +
                  `Could you provide more details?`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center rounded-full bg-[#D88546] px-6 py-3 text-sm font-semibold text-black transition hover:bg-[#e59252] md:w-auto"
              >
                Send Inquiry
              </MotionAnchor>
            </div>
          </div>
        </div>
      )}
    </FadeInSection>
  );
};

export default PropertiesResults;
